import React, { useEffect, useState, useMemo } from 'react';
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2, Search, Info } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { reconciliationApi } from '../services/api';

const eur = (v) =>
  v === null || v === undefined
    ? '—'
    : Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const Kpi = ({ label, value, sub, tone = 'slate' }) => {
  const tones = {
    slate: 'border-slate-200 bg-white',
    amber: 'border-amber-200 bg-amber-50',
    emerald: 'border-emerald-200 bg-emerald-50',
  };
  return (
    <div className={`rounded-lg border p-4 ${tones[tone] || tones.slate}`}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 font-mono font-bold tracking-tight text-2xl text-slate-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
};

const Section = ({ title, hint, count, children }) => (
  <div className="rounded-lg border border-slate-200 bg-white">
    <div className="flex items-baseline justify-between border-b border-slate-100 px-4 py-3">
      <h3 className="font-semibold text-slate-800">{title}</h3>
      <span className="text-xs text-slate-500">{count}</span>
    </div>
    {hint && <p className="px-4 pt-3 text-xs text-slate-500">{hint}</p>}
    <div className="p-4 pt-3">{children}</div>
  </div>
);

const Empty = ({ children }) => (
  <div className="flex items-center gap-2 py-4 text-sm text-slate-500">
    <CheckCircle2 className="h-4 w-4 text-emerald-500" /> {children}
  </div>
);

export default function ReconciliationView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await reconciliationApi.revenues();
      setData(res.data || res);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Abgleich konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const view = useMemo(() => {
    if (!data) return null;
    const hit = (s) => !q || String(s || '').toLowerCase().includes(q.toLowerCase());
    return {
      onlyOfffer: (data.onlyOfffer || []).filter((x) => hit(x.off)),
      onlyLexoffice: (data.onlyLexoffice || []).filter((x) => hit(x.lex)),
      deviations: (data.both || []).filter(
        (x) => x.diff !== null && Math.abs(x.diff) >= 0.5 && (hit(x.off) || hit(x.lex))
      ),
      both: (data.both || []).filter((x) => hit(x.off) || hit(x.lex)),
      websites: (data.websitesWithoutRevenue || []).filter((x) => hit(x.domainName) || hit(x.customerName)),
    };
  }, [data, q]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-16 text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin" /> Abgleich wird berechnet …
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {error}
        <Button variant="outline" size="sm" className="ml-3" onClick={load}>Erneut versuchen</Button>
      </div>
    );
  }

  const s = data.summary || {};

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kunde oder Domain suchen …" className="pl-9" />
        </div>
        <Button variant="outline" onClick={load} className="gap-2">
          <RefreshCw className="h-4 w-4" /> Neu berechnen
        </Button>
      </div>

      {!data.lexofficeEnabled && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <strong>Lexoffice ist nicht verbunden.</strong> {data.lexofficeHint}{' '}
            Ohne Verbindung siehst du nur die offfer-Seite — der Rechnungsabgleich bleibt leer.
          </div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="offfer laufend" value={eur(s.offferMonthly)} sub="pro Monat netto" />
        <Kpi label="Lexoffice laufend" value={eur(s.lexofficeMonthly)} sub="pro Monat netto (aktive Serienrechnungen)" />
        <Kpi
          label="Wird nicht fakturiert"
          value={eur(s.onlyOfffferMonthly)}
          sub={`${s.onlyOfffer || 0} Kunden ohne Serienrechnung`}
          tone={s.onlyOfffer ? 'amber' : 'emerald'}
        />
        <Kpi
          label="Fehlt in offfer"
          value={eur(s.onlyLexofficeMonthly)}
          sub={`${s.onlyLexoffice || 0} nur in Lexoffice`}
          tone={s.onlyLexoffice ? 'amber' : 'emerald'}
        />
      </div>

      <Section
        title="Einnahme in offfer, keine aktive Serienrechnung"
        hint="Diese Leistungen laufen, werden in Lexoffice aber nicht automatisch abgerechnet."
        count={`${view.onlyOfffer.length} Kunden`}
      >
        {view.onlyOfffer.length === 0 ? (
          <Empty>Alles fakturiert.</Empty>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="pb-2">Kunde</th>
                <th className="pb-2">Positionen</th>
                <th className="pb-2 text-right">pro Monat</th>
              </tr>
            </thead>
            <tbody>
              {view.onlyOfffer.map((r) => (
                <tr key={r.off} className="border-t border-slate-100 align-top">
                  <td className="py-2 pr-3 font-medium text-slate-800">
                    {r.off}
                    {r.lexGestoppt && (
                      <div className="text-xs font-normal text-amber-700">
                        In Lexoffice vorhanden, aber gestoppt: {r.lexGestoppt}
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-slate-600">
                    {r.positions.map((p, i) => (
                      <div key={i}>{p.titel || '—'} · {eur(p.betrag)} {p.turnus}</div>
                    ))}
                  </td>
                  <td className="py-2 text-right font-mono font-bold tracking-tight text-slate-900">{eur(r.monthly)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section
        title="Serienrechnung aktiv, Einnahme fehlt in offfer"
        hint="Wird abgerechnet, taucht aber nicht in Umsatz und Cashflow auf."
        count={`${view.onlyLexoffice.length} Kunden`}
      >
        {view.onlyLexoffice.length === 0 ? (
          <Empty>Keine Lücke.</Empty>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="pb-2">Kunde</th>
                <th className="pb-2">Serienrechnung</th>
                <th className="pb-2 text-right">pro Monat</th>
              </tr>
            </thead>
            <tbody>
              {view.onlyLexoffice.map((r) => (
                <tr key={r.lex} className="border-t border-slate-100 align-top">
                  <td className="py-2 pr-3 font-medium text-slate-800">
                    {r.lex}
                    {r.pausiertInOfffer && (
                      <div className="text-xs font-normal text-amber-700">
                        In offfer pausiert/archiviert: {r.pausiertInOfffer}
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-slate-600">
                    {r.positions.map((p, i) => (
                      <div key={i}>
                        {p.titel} · {eur(p.netto)} {p.turnus}
                        {p.naechste && <span className="text-slate-400"> · nächste {p.naechste}</span>}
                      </div>
                    ))}
                  </td>
                  <td className="py-2 text-right font-mono font-bold tracking-tight text-slate-900">{eur(r.monthly)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section
        title="Beträge weichen ab"
        hint="In beiden Systemen aktiv, aber der Monatswert stimmt nicht überein."
        count={`${view.deviations.length} Kunden`}
      >
        {view.deviations.length === 0 ? (
          <Empty>Alle Beträge stimmen überein.</Empty>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="pb-2">Kunde</th>
                <th className="pb-2 text-right">offfer</th>
                <th className="pb-2 text-right">Lexoffice</th>
                <th className="pb-2 text-right">Differenz</th>
              </tr>
            </thead>
            <tbody>
              {view.deviations.map((r) => (
                <tr key={r.lex} className="border-t border-slate-100">
                  <td className="py-2 pr-3 font-medium text-slate-800">
                    {r.off}
                    {r.off !== r.lex && <span className="text-xs font-normal text-slate-400"> ↔ {r.lex}</span>}
                  </td>
                  <td className="py-2 text-right font-mono">{eur(r.offMonthly)}</td>
                  <td className="py-2 text-right font-mono">{eur(r.lexMonthly)}</td>
                  <td className={`py-2 text-right font-mono font-bold ${r.diff > 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {r.diff > 0 ? '+' : ''}{eur(r.diff)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section
        title="Website vorhanden, aber keine laufende Einnahme"
        hint="Aus dem Domain-Register: Domain mit Website, zu der keine aktive Einnahme passt."
        count={`${view.websites.length} Domains`}
      >
        {view.websites.length === 0 ? (
          <Empty>Zu jeder Website läuft eine Einnahme.</Empty>
        ) : (
          <div className="flex flex-wrap gap-2">
            {view.websites.map((w) => (
              <div key={w.domainName} className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
                <div className="font-medium text-slate-800">{w.domainName}</div>
                {w.customerName && <div className="text-xs text-slate-500">{w.customerName}</div>}
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="In beiden Systemen aktiv" count={`${view.both.length} Kunden`}>
        {view.both.length === 0 ? (
          <div className="py-4 text-sm text-slate-500">Keine Treffer.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="pb-2">Kunde</th>
                <th className="pb-2 text-right">offfer / Monat</th>
                <th className="pb-2 text-right">Lexoffice / Monat</th>
              </tr>
            </thead>
            <tbody>
              {view.both.map((r) => (
                <tr key={r.lex} className="border-t border-slate-100">
                  <td className="py-2 pr-3 text-slate-800">{r.off}</td>
                  <td className="py-2 text-right font-mono">{eur(r.offMonthly)}</td>
                  <td className="py-2 text-right font-mono">{eur(r.lexMonthly)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {(data.lexInactive || []).length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
          <div>
            {data.lexInactive.length} Serienrechnungen in Lexoffice sind pausiert oder beendet und
            fließen hier nicht ein.
          </div>
        </div>
      )}
    </div>
  );
}
